import { DefaultLabField } from "@/services/backend/samples/components/sample-base-data-default-lab-field";
import { Kind } from "@/services/backend/samples/types/kind";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  PatchRequest,
  updateSamplesDustDensityBaseDataCache,
  useSamplesDustDensityBaseDataPatchMutation,
  useSamplesDustDensityBaseDataShowQuery,
} from "@/services/backend/samples/api/dust-density-base-data-api";
import { DustDensityBaseData } from "@/services/backend/samples/types/sample-base-data";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { TextInputField } from "@/shared/components/field/input-field";

/**
 * The patch carrying every editable value. Sending all of them — not just
 * the changed one — keeps a quick edit of a second field from replacing
 * the pending patch of the first one before it is sent.
 */
function patchOf(data: DustDensityBaseData): PatchRequest {
  return {
    id: data.id,
    label: data.label,
    unit: data.unit,
    referenceValue: data.referenceValue,
    labId: data.labId,
  };
}

export function DustDensityBaseDataFields({ id }: { id: UUID }) {
  const {
    data,
    error: dataError,
    isLoading: dataLoading,
  } = useSamplesDustDensityBaseDataShowQuery({ id });

  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateSamplesDustDensityBaseDataCache,
    useSamplesDustDensityBaseDataPatchMutation(),
  );

  if (dataError) {
    return <RTKQueryErrorAlert error={dataError} />;
  }

  if (dataLoading || !data) {
    return <CardContentSkeleton />;
  }

  const edit = (change: Partial<DustDensityBaseData>) => {
    const next = { ...data, ...change };
    set(patchOf(next), next);
  };

  return (
    <div className="grid gap-4">
      {/* The label is unique per company, so a rename can be rejected
          with a conflict rather than a field error. */}
      <RTKQueryErrorAlert error={error} />
      <TextInputField
        label={t("Bezeichnung")}
        description={t(
          "Unterscheidet die Basisdaten eines Unternehmens voneinander.",
        )}
        text={data.label}
        onTextChange={(label) => edit({ label })}
        errors={inputValidationErrorMessages(error, "label")}
      />
      <TextInputField
        label={t("Einheit")}
        text={data.unit}
        onTextChange={(unit) => edit({ unit })}
        errors={inputValidationErrorMessages(error, "unit")}
      />
      <TextInputField
        label={t("Referenzwert")}
        text={data.referenceValue}
        onTextChange={(referenceValue) => edit({ referenceValue })}
        errors={inputValidationErrorMessages(error, "referenceValue")}
      />
      <DefaultLabField
        kind={Kind.DustDensity}
        labId={data.labId}
        onLabIdChange={(labId) => edit({ labId })}
        errors={inputValidationErrorMessages(error, "labId")}
      />
    </div>
  );
}
